import { useCallback, useEffect, useMemo, useState } from 'react';
import { createHttpClient } from '@wix/http-client';

import { useInitialState } from '../contexts/InitialStateContext';
import { Template } from '../domain/entities/template';
import { useSelectedCategoryId } from './useSelectedCategoryId';
import { HttpTemplateRepository } from '../domain/repositories/HttpTemplateRepository';
import { useFedopsLogger } from '../contexts/FedopsLoggerContext';
import { TEMPLATE_LOADING_SCHEMA } from '../consts';
import { useExperiments } from '../contexts/ExperimentsContext';
import { useSearch } from './useSearch';
import { useBiLogger } from '../contexts/BILoggerContext';

type State = {
  templates: Template[];
  categoryId?: string;
  criteria?: string;
  offset: number;
  loading: boolean;
  isFullyLoaded: boolean;
  error: Error | null;
};

export const useTemplates = () => {
  const initialState = useInitialState();
  const fedopsLogger = useFedopsLogger();
  const biLogger = useBiLogger();
  const experiments = useExperiments();
  const { criteria: selectedCriteria, isSearchActive } = useSearch();

  const isLazyLoadingForTemplatesUsed = experiments.is(
    'specs.funnel.StudioTemplatesTemplateLazyLoading',
    'true',
  );

  const [state, setState] = useState<State>({
    templates: initialState.templates,
    categoryId: initialState.categoryId,
    criteria: initialState.criteria,
    offset: initialState.templates.length,
    loading: false,
    isFullyLoaded: false,
    error: null,
  });

  const categories = useMemo(
    () => initialState.book?.categories ?? [],
    [initialState.book],
  );
  const selectedCategoryId = useSelectedCategoryId({ categories });

  const loadTemplates = useCallback(
    (categoryId: string, limit: number, offset: number) => {
      const templateRepository = new HttpTemplateRepository(createHttpClient());
      setState({
        ...state,
        error: null,
        loading: true,
      });
      templateRepository
        .loadTemplates({
          categoryId,
          language: initialState.locale,
          limit,
          offset,
        })
        .then((domainTemplates) => {
          const allTemplates = [
            ...(categoryId === state.categoryId ? state.templates : []),
            ...domainTemplates,
          ];

          setState({
            templates: allTemplates,
            categoryId,
            criteria: undefined,
            offset: allTemplates.length,
            loading: false,
            isFullyLoaded: domainTemplates.length === 0,
            error: null,
          });
        })
        .finally(() => fedopsLogger.stopActiveInteraction());
    },
    [fedopsLogger, initialState.locale, state],
  );

  const searchTemplates = useCallback(
    (criteria: string, limit: number, offset: number) => {
      const templateRepository = new HttpTemplateRepository(createHttpClient());
      setState({
        ...state,
        error: null,
        loading: true,
      });

      templateRepository
        .searchTemplates({
          query: criteria,
          language: initialState.locale,
          limit,
          offset,
        })
        .then(({ templates: domainTemplates, total }) => {
          const allTemplates = [
            ...(criteria === state.criteria ? state.templates : []),
            ...domainTemplates,
          ];

          // we don't need to send search event on load more
          if (offset === 0) {
            biLogger.reportSearch({ criteria, total });
          }

          setState({
            templates: allTemplates,
            categoryId: undefined,
            offset: allTemplates.length,
            criteria,
            loading: false,
            isFullyLoaded: domainTemplates.length === 0,
            error: null,
          });
        })
        .catch((error) => {
          console.error(error);
          window?.Sentry?.captureException(error);
          setState((prevState) => ({ ...prevState, error }));
        })
        .finally(() =>
          setState((prevState) => ({ ...prevState, loading: false })),
        );
    },
    [biLogger, initialState.locale, state],
  );

  const loadMore = useCallback(
    (limit: number) => {
      if (!state.loading && !state.isFullyLoaded) {
        if (isSearchActive && selectedCriteria === state.criteria) {
          searchTemplates(selectedCriteria, limit, state.offset);
        } else if (
          selectedCategoryId === state.categoryId &&
          state.categoryId
        ) {
          loadTemplates(state.categoryId, limit, state.offset);
        }
      }
    },
    [
      state.loading,
      state.isFullyLoaded,
      state.criteria,
      state.categoryId,
      state.offset,
      isSearchActive,
      selectedCriteria,
      selectedCategoryId,
      searchTemplates,
      loadTemplates,
    ],
  );

  useEffect(() => {
    if (
      isSearchActive &&
      selectedCriteria !== state.criteria &&
      !state.loading
    ) {
      searchTemplates(
        selectedCriteria,
        isLazyLoadingForTemplatesUsed ? TEMPLATE_LOADING_SCHEMA.INITIAL : 100,
        0,
      );
    }
  }, [selectedCriteria]);

  useEffect(() => {
    if (
      !isSearchActive &&
      selectedCategoryId !== state.categoryId &&
      selectedCategoryId !== undefined &&
      !state.loading
    ) {
      loadTemplates(
        selectedCategoryId,
        isLazyLoadingForTemplatesUsed ? TEMPLATE_LOADING_SCHEMA.INITIAL : 100,
        0,
      );
    }
  }, [
    isLazyLoadingForTemplatesUsed,
    loadTemplates,
    selectedCategoryId,
    state.categoryId,
    state.loading,
    state.offset,
    isSearchActive,
  ]);

  useEffect(() => {
    if (
      !isLazyLoadingForTemplatesUsed &&
      !isSearchActive &&
      state.offset === 100
    ) {
      loadMore(100);
    }
  }, [isLazyLoadingForTemplatesUsed, loadMore, state.offset]);

  return {
    templates: state.templates,
    loadMore,
    loading: state.loading,
    error: state.error,
  };
};
